import Appearance from "../shapes/Appearance";
import AbstractProvidedClass from "../shared/AbstractProvidedClass";
import { DEFAULT_FILL_COLOR, DEFAULT_STROKE_COLOR } from "../shared/constants";
import EventTypes from "../shared/EventTypes";
import ModeSelector from "../sidebar/edit/Fields/ModeSelector";
import { BRING_FORWARD, BRING_TO_FRONT, CHANGE_FILL_COLOR, CHANGE_STROKE_COLOR, CHANGE_STROKE_WIDTH, SEND_BACKWARD, SEND_TO_BACK, TOGGLE_FILL, TOGGLE_STROKE } from "../tools/actions/ActionHelper";

export default class DrawMenu extends AbstractProvidedClass {
    appearance = null;
    mode = null;

    constructor(provider) {
        super(provider);

        this.appearance = new Appearance(this.provider, {
            fillColor: this.p.color(DEFAULT_FILL_COLOR),
            strokeColor: this.p.color(DEFAULT_STROKE_COLOR),
            strokeWidth: null,
        });

        this.container = this.p.createDiv()
            .parent('#p5canvas')
            .class('planner-toolbar-vert');

        this.createColorTools();
        this.createStrokeWidth();
        this.createCopyAppearanceButton();
        this.createModeSelector();
        this.createOrderButtons();

        this.on(EventTypes.UPDATE_UNIT_SYSTEM, (isMetric) => {
            this.strokeWidthUnit.html(isMetric ? 'm' : 'ft');
            if (this.appearance.strokeWidth) {
                this.strokeWidthInput.value(this.pixelsToDisplay(this.appearance.strokeWidth, isMetric));
            }
        });

        this.hide();
    }

    createColorTools() {
        // Fill color
        let fillLabelRow = this.p.createDiv()
            .parent(this.container)
            .class('row row-between');
        this.p.createElement('h4', 'Fill')
            .parent(fillLabelRow)
            .class('input-label');
        this.useFill = this.p.createCheckbox(null, true)
            .parent(fillLabelRow)
            .style('display', 'flex')
            .changed(() => this.changeUseFill());
        this.useFill.elt.firstChild.firstChild.id = 'use-fill';
        this.fillPicker = this.p.createColorPicker(DEFAULT_FILL_COLOR)
            .parent(this.container)
            .class('input-color')
            .mousePressed(() => {
                if (this.mode == 'element') {
                    this.action ??= this.actions.new(CHANGE_FILL_COLOR, this.appearance, this.fillPicker)
                }
            })
            .input(() => this.changeFillColor())
            .changed(() => this.mode == 'element' ? this.finishAction() : null);

        // Stroke color
        let strokeLabelRow = this.p.createDiv()
            .parent(this.container)
            .class('row row-between');
        this.p.createElement('h4', 'Stroke')
            .parent(strokeLabelRow)
            .class('input-label');
        this.useStroke = this.p.createCheckbox(null, true)
            .parent(strokeLabelRow)
            .style('display', 'flex')
            .changed(() => this.changeUseStroke());
        this.useStroke.elt.firstChild.firstChild.id = 'use-stroke';
        this.strokePicker = this.p.createColorPicker(DEFAULT_STROKE_COLOR)
            .parent(this.container)
            .class('input-color')
            .mousePressed(() => {
                if (this.mode == 'element') {
                    this.action ??= this.actions.new(CHANGE_STROKE_COLOR, this.appearance, this.strokePicker)
                }
            })
            .input(() => this.changeStrokeColor())
            .changed(() => this.mode == 'element' ? this.finishAction() : null);
    }

    sliderToWidth(raw) {
        const t = raw / 1000;
        return Math.round(Math.pow(1000, t) * 10) / 10;
    }

    widthToSlider(width) {
        return Math.round(Math.log(width) / Math.log(1000) * 1000);
    }

    pixelsToDisplay(px, isMetric = null) {
        return this.calculatePixelToDisplayDistance(px, isMetric);
    }

    displayToPixels(val) {
        const factor = this.settings.metric ? 100 : 30.48;
        return Math.round(val * factor * 10) / 10;
    }

    createStrokeWidth() {
        let widthRow = this.p.createDiv()
            .parent(this.container)
            .class('row row-between');
        this.p.createElement('h4', 'Width')
            .parent(widthRow)
            .class('input-label');
        this.useStrokeWidth = this.p.createCheckbox(null, false)
            .parent(widthRow)
            .style('display', 'flex')
            .changed(() => this.toggleStrokeWidth());
        let inputWrapper = this.p.createDiv()
            .parent(this.container)
            .style('position', 'relative')
            .style('width', '100%');
        this.strokeWidthInput = this.p.createInput('0.01', 'text')
            .parent(inputWrapper)
            .attribute('inputmode', 'decimal')
            .style('width', '100%')
            .style('box-sizing', 'border-box')
            .style('padding-right', '2rem')
            .class('input-text')
            .attribute('disabled', true)
            .input(() => {
                this.changeStrokeWidth();
                const pixels = this.displayToPixels(parseFloat(this.strokeWidthInput.value()) || 0.01);
                this.strokeWidthSlider.value(this.widthToSlider(pixels));
            });
        this.strokeWidthInput.elt.onfocus = () => { this.enableHotKeys = false; };
        this.strokeWidthInput.elt.onblur = () => { this.enableHotKeys = true; };
        this.strokeWidthUnit = this.p.createSpan('m')
            .parent(inputWrapper)
            .style('position', 'absolute')
            .style('right', '0.5rem')
            .style('top', '50%')
            .style('transform', 'translateY(-50%)')
            .style('pointer-events', 'none')
            .class('input-label');
        this.strokeWidthSlider = this.p.createSlider(0, 1000, 0, 1)
            .parent(this.container)
            .style('width', '100%')
            .attribute('disabled', true)
            .mousePressed(() => {
                if (this.mode === 'element') {
                    this.action ??= this.actions.new(CHANGE_STROKE_WIDTH, this.appearance, this.appearance.strokeWidth ?? 1);
                }
            })
            .touchStarted(() => {
                if (this.mode === 'element') {
                    this.action ??= this.actions.new(CHANGE_STROKE_WIDTH, this.appearance, this.appearance.strokeWidth ?? 1);
                }
            })
            .input(() => {
                const pixels = this.sliderToWidth(this.strokeWidthSlider.value());
                this.strokeWidthInput.value(this.pixelsToDisplay(pixels));
                this.mode === 'element'
                    ? this.action?.update(pixels)
                    : this.appearance.strokeWidth = pixels;
            })
            .mouseReleased(() => this.finishAction())
            .touchEnded(() => this.finishAction());
    }

    toggleStrokeWidth() {
        const active = this.useStrokeWidth.checked();
        if (active) {
            this.strokeWidthInput.removeAttribute('disabled');
            this.strokeWidthSlider.removeAttribute('disabled');
        } else {
            this.strokeWidthInput.attribute('disabled', true);
            this.strokeWidthSlider.attribute('disabled', true);
        }
        const value = active ? (this.displayToPixels(parseFloat(this.strokeWidthInput.value()) || 0.01)) : null;
        if (this.mode === 'element') {
            this.actions.execute(CHANGE_STROKE_WIDTH, this.appearance, value);
        } else {
            this.appearance.strokeWidth = value;
        }
    }

    changeStrokeWidth() {
        const value = this.displayToPixels(parseFloat(this.strokeWidthInput.value()) || 0.01);
        if (this.mode === 'element') {
            this.actions.execute(CHANGE_STROKE_WIDTH, this.appearance, value);
        } else {
            this.appearance.strokeWidth = value;
        }
    }

    finishAction() {
        this.action?.finish();
        this.action = null;
    }

    createModeSelector() {
        this.modeSelector = ModeSelector.setup(this.provider)
            .parent(this.container)
            .make()
            .setDrawMode('rectangle');
    }

    createCopyAppearanceButton() {
        this.copyAppearanceRow = this.p.createDiv()
            .parent(this.container)
            .class('row');
        this.copyAppearanceBtn = this.p.createDiv()
            .parent(this.copyAppearanceRow)
            .class('btn-icon-clear')
            .style('display', 'flex')
            .style('align-items', 'center')
            .style('justify-content', 'center')
            .mousePressed(() => this.tools.setTool('copy_style', this.appearance));
        if (!this.touch) {
            this.copyAppearanceBtn.attribute('data-tooltip', 'Copy style');
        }
        let svg = SVG().addTo(this.copyAppearanceBtn.elt);
        svg.use('file-copy-line', '/icons/__remixicon.sprite.svg');
        this.copyAppearanceBtn.elt.children[0].classList.add('icon');
        this.copyAppearanceRow.hide();
    }

    createOrderButtons() {
        this.orderButtons = this.p.createDiv()
            .parent(this.container);
        let orderLabel = this.p.createElement('h4', 'Position')
            .class('input-label')
            .parent(this.orderButtons);

        this.buttonContainer = this.p.createDiv()
            .parent(this.orderButtons)
            .class('row row-between');

        this.createOrderButton(
            'Bring to front',
            () => this.actions.execute(BRING_TO_FRONT, this.selection.first),
            'bring-to-front'
        );
        this.createOrderButton(
            'Send to back',
            () => this.actions.execute(SEND_TO_BACK, this.selection.first),
            'send-to-back'
        );
        this.createOrderButton(
            'Bring forward',
            () => this.actions.execute(BRING_FORWARD, this.selection.first),
            'bring-forward'
        );
        this.createOrderButton(
            'Send backward',
            () => this.actions.execute(SEND_BACKWARD, this.selection.first),
            'send-backward'
        );
    }

    createOrderButton(tooltip, func, svgId) {
        let btn = this.p.createDiv()
            .parent(this.buttonContainer)
            .mouseClicked(func)
            .class('btn-icon-clear');
        if (!this.touch) {
            btn.attribute('data-tooltip', tooltip);
        }
        let svg = SVG().addTo(btn.elt);
        svg.use(svgId, '/icons/__remixicon.sprite.svg');
        btn.elt.children[0].classList.add('icon');

        return btn;
    }

    disableFill() {
        this.fillWasChecked = this.useFill.checked()
        this.fill = false;
        this.useFill.elt.firstChild.firstChild.disabled = true;
        this.fillPicker.attribute('disabled', true);
    }

    enableFill() {
        this.fill = this.fillWasChecked;
        this.useFill.elt.firstChild.firstChild.disabled = false;
        if (this.useFill.checked()) {
            this.fillPicker.removeAttribute('disabled');
        }
    }

    changeUseFill() {
        this.useFill.checked()
            ? this.fillPicker.removeAttribute('disabled')
            : this.fillPicker.attribute('disabled', true);
        if (this.mode == 'element') {
            this.actions.execute(
                TOGGLE_FILL,
                this.appearance, this.fillPicker.color(), this.useFill.checked()
            );
        } else {
            this.useFill.checked()
                ? this.appearance.fillColor = this.fillColor
                : this.appearance.fillColor = null;
        }
    }

    changeUseStroke() {
        const strokeOn = this.useStroke.checked();
        strokeOn
            ? this.strokePicker.removeAttribute('disabled')
            : this.strokePicker.attribute('disabled', true);

        if (strokeOn) {
            const wasChecked = this.strokeWidthWasChecked ?? false;
            this.useStrokeWidth.checked(wasChecked);
            if (wasChecked) {
                this.strokeWidthInput.removeAttribute('disabled');
                this.strokeWidthSlider.removeAttribute('disabled');
            }
        } else {
            this.strokeWidthWasChecked = this.useStrokeWidth.checked();
            this.useStrokeWidth.checked(false);
            this.strokeWidthInput.attribute('disabled', true);
            this.strokeWidthSlider.attribute('disabled', true);
        }

        if (this.mode == 'element') {
            this.actions.execute(
                TOGGLE_STROKE,
                this.appearance, this.strokePicker.color(), strokeOn
            );
        } else {
            strokeOn
                ? this.appearance.strokeColor = this.strokeColor
                : this.appearance.strokeColor = null;
        }
    }

    changeFillColor() {
        this.appearance.fillColor = this.fillColor;
    }

    changeStrokeColor() {
        this.appearance.strokeColor = this.strokeColor;
    }

    setAppearance(appearance) {
        this.appearance = appearance;
        if (this.appearance.fillColor) {
            this.useFill.checked(true);
            this.fillPicker.value(this.appearance.fillColor.toString('#rrggbb'));
            this.fillPicker.removeAttribute('disabled');
        } else {
            this.useFill.checked(false);
            this.fillPicker.attribute('disabled', true);
        }

        if (this.appearance.strokeColor) {
            this.useStroke.checked(true);
            this.strokePicker.value(this.appearance.strokeColor.toString('#rrggbb'));
            this.strokePicker.removeAttribute('disabled');
        } else {
            this.useStroke.checked(false);
            this.strokePicker.attribute('disabled', true);
        }

        const width = this.appearance.strokeWidth ?? 1;
        this.strokeWidthInput.value(this.pixelsToDisplay(width));
        this.strokeWidthSlider.value(this.widthToSlider(width));
        if (this.appearance.strokeWidth) {
            this.useStrokeWidth.checked(true);
            this.strokeWidthInput.removeAttribute('disabled');
            this.strokeWidthSlider.removeAttribute('disabled');
        } else {
            this.useStrokeWidth.checked(false);
            this.strokeWidthInput.attribute('disabled', true);
            this.strokeWidthSlider.attribute('disabled', true);
        }
    }

    show(mode) {
        this.container.show();
        this.mode = mode;
        if (mode == 'draw') {
            this.modeSelector.show();
            this.orderButtons.hide();
            this.copyAppearanceRow.hide();
        } else {
            this.modeSelector.hide();
            this.orderButtons.show();
            this.copyAppearanceRow.show();
        }
    }

    hide() {
        this.appearance = this.appearance.clone();
        this.container.hide();
    }

    get fillColor() {
        return this.p.color(this.fillPicker.color());
    }

    get strokeColor() {
        return this.p.color(this.strokePicker.color());
    }

    get stroke() {
        return this.useStroke.checked() == 1;
    }

    set stroke(stroke) {
        this.useStroke.checked(stroke ? 1 : 0);
        this.changeUseStroke();
    }

    get fill() {
        return this.useFill.checked() == 1;
    }

    set fill(fill) {
        this.useFill.checked(fill ? 1 : 0);
        this.changeUseFill();
    }
}
